import React, { useState } from 'react';
import { Tooltip } from 'antd';
import { IFabricationOrder } from '../../../../../store/ducks/fabrication-orders/types';
import { ActionsContainer } from '../styles';
import {
  EFabricationContractsStatus,
  IFabricationContract,
  ILastOrderDetails
} from '../../../../../store/ducks/fabrication-contracts/types';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../../store';
import { Button } from '@gerdau/hefesto/Button';
import { Icon } from '@gerdau/hefesto/Icon';
import { Modal } from '@gerdau/hefesto/Modal';
import { getTaggingInformationScreen } from '../../../../../utils/Tagging';
import { sendTagging } from '../../../../../store/ducks/tagging/actions';
import { useLocation } from 'react-router-dom';
import { ChooseBetweenNewOrReviewOrderModal } from '../../../../../components/ChooseBetweenNewOrReviewOrderModal';

interface IFabricationOrderDetailsActionsButtonsProps {
  fabricationOrder: IFabricationOrder;
  selectedContract: IFabricationContract | undefined;
  onBuyAgainClick: (fabricationOrder: IFabricationOrder) => void;
  goToPurchaseFabrication(): void;
  onClickDownloadPDF: () => void;
  loadingPDF?: boolean;
  disabled?: boolean;
}

const FabricationOrderDetailsActionsButtons: React.FC<
  IFabricationOrderDetailsActionsButtonsProps
> = ({
  fabricationOrder,
  selectedContract,
  onBuyAgainClick,
  goToPurchaseFabrication,
  onClickDownloadPDF,
  loadingPDF,
  disabled
}) => {
  const { pathname } = useLocation();
  const dispatch = useDispatch();

  const userState = useSelector((appState: ApplicationState) => appState.user);

  const [
    showChooseBetweenNewOrReviewModal,
    setShowChooseBetweenNewOrReviewModal
  ] = useState<ILastOrderDetails>();

  const onBuyAgainClickedButton = (order: ILastOrderDetails) => {
    setShowChooseBetweenNewOrReviewModal(order);
  };

  const closeChooseBetweenNewOrReviewModal = () => {
    setShowChooseBetweenNewOrReviewModal(undefined);
  };

  const [
    canShowFabricationOrderInProgressModal,
    setCanShowFabricationOrderInProgressModal
  ] = useState(false);
  const fabricationPurchaseOrder = useSelector(
    (appState: ApplicationState) => appState.fabricationPurchaseOrder
  );
  const hasFabricationOrderInProgress: boolean =
    fabricationPurchaseOrder.constructionIdentification.contractNumber !==
      undefined ||
    fabricationPurchaseOrder.constructionIdentification.orderNumber !== '' ||
    fabricationPurchaseOrder.constructionIdentification.desiredDate !==
      undefined;

  const canBuyAgain = (): boolean => {
    if (userState.isManager) {
      return true;
    }

    return (
      selectedContract !== undefined &&
      selectedContract.status === EFabricationContractsStatus.OPEN
    );
  };

  const hasSelectedContractOrderInputAccess = (): boolean => {
    return (
      selectedContract !== undefined &&
      userState.userPermissions.hasOrderInputAccess
    );
  };

  const getTooltipMessage = () => {
    if (!selectedContract) return;

    const contractMessageError: {
      [key in EFabricationContractsStatus]: string;
    } = {
      [EFabricationContractsStatus.CLOSED]:
        'Não é possível realizar compras após o encerramento do contrato.',
      [EFabricationContractsStatus.NEW_CONTRACT]:
        'Não é possível realizar compras em contratos com status "Novo contrato".',
      Todos: '',
      Aberto: ''
    };

    return contractMessageError[selectedContract.status];
  };

  const { screenName = '', itemDescription = '' } = getTaggingInformationScreen(
    pathname,
    true
  );

  return (
    <ActionsContainer isNewTitleFacelift>
      <div className="actions">
        <Button
          name="downloadOrderPDF"
          onClick={onClickDownloadPDF}
          loading={loadingPDF}
          disabled={disabled}
          icon={<Icon option="Download" />}
          variant="primary"
        >
          Baixar resumo do pedido
        </Button>

        {hasSelectedContractOrderInputAccess() && (
          <Tooltip
            overlayClassName="repeated-order-number"
            placement="top"
            title={getTooltipMessage()}
            trigger={['hover']}
          >
            <Button
              name="buyOrderAgain"
              disabled={!canBuyAgain()}
              onClick={() => {
                if (hasFabricationOrderInProgress) {
                  setCanShowFabricationOrderInProgressModal(true);
                  dispatch(
                    sendTagging({
                      screenName: `${screenName}:DetalhesDoPedido:ModalRecomprarPedido`,
                      page: location.pathname,
                      event: 'page_view'
                    })
                  );
                } else {
                  onBuyAgainClick && onBuyAgainClickedButton(fabricationOrder);
                }
              }}
              icon={<Icon option="Cart" />}
              variant="secondary"
            >
              Recomprar este pedido
            </Button>
          </Tooltip>
        )}

        {onBuyAgainClick !== undefined &&
          hasFabricationOrderInProgress &&
          canShowFabricationOrderInProgressModal && (
            <Modal
              title="Você possui um pedido em andamento"
              modalSize="lg"
              onClose={() => {
                setCanShowFabricationOrderInProgressModal(false);
              }}
              buttons={{
                left: {
                  name: 'resume-order',
                  onClick: () => {
                    dispatch(
                      sendTagging({
                        screenName: `${screenName}:DetalhesDoPedido:ModalRecomprarPedido`,
                        itemName: 'button:retornar_pedido',
                        event: 'click',
                        itemDescription: `${itemDescription}detalhesDoPedido_modalRecomprarPedido_retornarPedido`
                      })
                    );
                    goToPurchaseFabrication();
                  },
                  children: 'Retomar pedido'
                },

                right: {
                  name: 'continue-order',
                  onClick: () => {
                    dispatch(
                      sendTagging({
                        screenName: `${screenName}:DetalhesDoPedido:ModalRecomprarPedido`,
                        itemName: 'button:continuar',
                        event: 'click',
                        itemDescription: `${itemDescription}detalhesDoPedido_modalRecomprarPedido_continuar`
                      })
                    );
                    onBuyAgainClick(fabricationOrder);
                  },
                  children: 'Continuar'
                }
              }}
            >
              <span>
                Ao clicar em Continuar você perderá as informações preenchidas.
                Caso opte por retomar pedido, você será redirecionado para o
                pedido em andamento.
              </span>
            </Modal>
          )}
      </div>
      {showChooseBetweenNewOrReviewModal && (
        <ChooseBetweenNewOrReviewOrderModal
          onCloseModal={closeChooseBetweenNewOrReviewModal}
          order={showChooseBetweenNewOrReviewModal}
          onChooseCreate={() =>
            onBuyAgainClick(
              showChooseBetweenNewOrReviewModal as IFabricationOrder
            )
          }
          from={'FabricationOrderDetailsActionsButtons'}
          payload={fabricationOrder}
        />
      )}
    </ActionsContainer>
  );
};

export default FabricationOrderDetailsActionsButtons;
